import {
  parseAdminStudent,
  parseAdminStudentsResponse,
  parseCreateAdminStudentRequest,
  parseCreateEnrollmentRequest,
  parseCreateFamilyAccountRequest,
  parseEnrollment,
  parseEnrollmentsResponse,
  parseFamilyAccount,
  parseFamilyAccountsResponse,
  parseTransitionEnrollmentStatusRequest,
  parseUpdateAdminStudentRequest,
  parseUpdateEnrollmentRequest,
  parseUpdateFamilyAccountRequest,
  parseConvertEnrollmentToRegularRequest
} from "@madamuh/domain";
import type {
  AdminStudentDto,
  AdminStudentsResponseDto,
  CreateAdminStudentRequestDto,
  CreateEnrollmentRequestDto,
  CreateFamilyAccountRequestDto,
  EnrollmentDto,
  EnrollmentsResponseDto,
  FamilyAccountDto,
  FamilyAccountsResponseDto,
  TransitionEnrollmentStatusRequestDto,
  UpdateAdminStudentRequestDto,
  UpdateEnrollmentRequestDto,
  UpdateFamilyAccountRequestDto,
  ConvertEnrollmentToRegularRequestDto
} from "@madamuh/domain";
import { apiGet, apiPatch, apiPost } from "./client";

export async function fetchAdminStudents(tenantSlug: string): Promise<AdminStudentsResponseDto> {
  const response = await apiGet<unknown>(`/api/v1/admin/tenants/${tenantSlug}/students`);
  return parseAdminStudentsResponse(response);
}

export async function fetchAdminStudent(tenantSlug: string, studentId: string): Promise<AdminStudentDto> {
  const response = await apiGet<unknown>(`/api/v1/admin/tenants/${tenantSlug}/students/${studentId}`);
  return parseAdminStudent(response);
}

export async function createAdminStudent(
  tenantSlug: string,
  request: CreateAdminStudentRequestDto
): Promise<AdminStudentDto> {
  const parsedRequest = parseCreateAdminStudentRequest(request);
  const response = await apiPost<CreateAdminStudentRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/students`,
    parsedRequest
  );

  return parseAdminStudent(response);
}

export async function patchAdminStudent(
  tenantSlug: string,
  studentId: string,
  request: UpdateAdminStudentRequestDto
): Promise<AdminStudentDto> {
  const parsedRequest = parseUpdateAdminStudentRequest(request);
  const response = await apiPatch<UpdateAdminStudentRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/students/${studentId}`,
    parsedRequest
  );

  return parseAdminStudent(response);
}

export async function fetchFamilyAccounts(tenantSlug: string): Promise<FamilyAccountsResponseDto> {
  const response = await apiGet<unknown>(`/api/v1/admin/tenants/${tenantSlug}/family-accounts`);
  return parseFamilyAccountsResponse(response);
}

export async function fetchFamilyAccount(tenantSlug: string, familyAccountId: string): Promise<FamilyAccountDto> {
  const response = await apiGet<unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/family-accounts/${familyAccountId}`
  );
  return parseFamilyAccount(response);
}

export async function createFamilyAccount(
  tenantSlug: string,
  request: CreateFamilyAccountRequestDto
): Promise<FamilyAccountDto> {
  const parsedRequest = parseCreateFamilyAccountRequest(request);
  const response = await apiPost<CreateFamilyAccountRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/family-accounts`,
    parsedRequest
  );

  return parseFamilyAccount(response);
}

export async function patchFamilyAccount(
  tenantSlug: string,
  familyAccountId: string,
  request: UpdateFamilyAccountRequestDto
): Promise<FamilyAccountDto> {
  const parsedRequest = parseUpdateFamilyAccountRequest(request);
  const response = await apiPatch<UpdateFamilyAccountRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/family-accounts/${familyAccountId}`,
    parsedRequest
  );

  return parseFamilyAccount(response);
}

export async function fetchAdminEnrollments(tenantSlug: string): Promise<EnrollmentsResponseDto> {
  const response = await apiGet<unknown>(`/api/v1/admin/tenants/${tenantSlug}/enrollments`);
  return parseEnrollmentsResponse(response);
}

export async function createEnrollment(
  tenantSlug: string,
  request: CreateEnrollmentRequestDto
): Promise<EnrollmentDto> {
  const parsedRequest = parseCreateEnrollmentRequest(request);
  const response = await apiPost<CreateEnrollmentRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/enrollments`,
    parsedRequest
  );

  return parseEnrollment(response);
}

export async function patchEnrollment(
  tenantSlug: string,
  enrollmentId: string,
  request: UpdateEnrollmentRequestDto
): Promise<EnrollmentDto> {
  const parsedRequest = parseUpdateEnrollmentRequest(request);
  const response = await apiPatch<UpdateEnrollmentRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/enrollments/${enrollmentId}`,
    parsedRequest
  );

  return parseEnrollment(response);
}

export async function transitionEnrollment(
  tenantSlug: string,
  enrollmentId: string,
  request: TransitionEnrollmentStatusRequestDto
): Promise<EnrollmentDto> {
  const parsedRequest = parseTransitionEnrollmentStatusRequest(request);
  const response = await apiPost<TransitionEnrollmentStatusRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/enrollments/${enrollmentId}/transition`,
    parsedRequest
  );

  return parseEnrollment(response);
}

export async function convertEnrollmentToRegular(
  tenantSlug: string,
  enrollmentId: string,
  request: ConvertEnrollmentToRegularRequestDto
): Promise<EnrollmentDto> {
  const parsedRequest = parseConvertEnrollmentToRegularRequest(request);
  const response = await apiPost<ConvertEnrollmentToRegularRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/enrollments/${enrollmentId}/convert-to-regular`,
    parsedRequest
  );

  return parseEnrollment(response);
}
